import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { FaWhatsapp, FaPlug, FaQrcode, FaChrome, FaDownload, FaArrowLeft, FaShieldAlt, FaKey } from "react-icons/fa";
import api from "../utils/axiosInstance";
import { useSession } from "../context/SessionContext";
import { socket } from "../socket";
import { useSubscription } from "../context/SubscriptionContext";
import { useAuth } from "../context/AuthContext";
import { useModal } from "../context/ModalContext";
import {
  Card, CardHeader, CardBody, Button, Alert, PageHeader,
  TableWrapper, Table, Th, Td
} from "../components/ui";
import OnboardingTip from "../components/ui/OnboardingTip";
import ManagedProxyAddonCard from "../components/ManagedProxyAddonCard";

// Direct download URL for the Walytic Browser Relay extension.
// TODO(biswajit): replace with the live Chrome Web Store link once published.
const EXTENSION_DOWNLOAD_URL = "https://chromewebstore.google.com/detail/walytic-browser-relay";

const WhatsappPhones = () => {
  const modal = useModal();
  const { teamRole } = useAuth();
  const isTeamMember = teamRole === "member";
  const [phone, setPhone] = useState("");
  const [connectMethod, setConnectMethod] = useState(null); // null | 'qr' | 'extension'
  const [proxyUrl, setProxyUrl] = useState("");
  const [proxyMode, setProxyMode] = useState("managed");
  const [managedProxy, setManagedProxy] = useState(null);
  const [proxyTesting, setProxyTesting] = useState(false);
  const [testingProxySession, setTestingProxySession] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [qrSessionId, setQrSessionId] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const { setSessionId } = useSession();
  const { subscription } = useSubscription();

  const currentSessionRef = useRef(null);
  const pendingSessionRef = useRef(null); // tracks session that showed QR but never connected
  const connectInFlightRef = useRef(false);

  const usageByKey = subscription?.usageByKey || {};
  const maxConnections = usageByKey.whatsappConnection?.total ?? 0;
  const usedConnections = usageByKey.whatsappConnection?.used || 0;
  const isExpired = subscription?.subscription?.status === "expired";
  const connectionQuotaReached = maxConnections !== -1 && usedConnections >= maxConnections;

  const normalizePhone = (raw) => (raw || "").replace(/\D/g, "");

  const displayQr = (qr, sessionId) => {
    if (!qr || !sessionId) return;
    setConnectMethod("qr");
    setQrCode(qr);
    setQrSessionId(sessionId);
    setQrLoading(false);
    setLoginSuccess(false);
    pendingSessionRef.current = sessionId;
    currentSessionRef.current = sessionId;
  };

  const fetchSessions = async () => {
    try {
      const res = await api.get("/api/sessions");
      const data = res?.data?.data ?? res?.data ?? [];
      const webSessions = Array.isArray(data)
        ? data.filter((session) => (session.transportType || "walytic_web") !== "walytic_cloud")
        : [];
      setSessions(webSessions);

      // Recover an in-progress QR after refresh/remount. The worker caches the
      // latest QR for this exact race; previously we polled only from the old
      // component instance, so a background entitlement refresh could leave a
      // pending row visible while the QR itself remained hidden.
      const pending = webSessions.find((session) => session.status === "pending");
      if (pending && !pendingSessionRef.current) {
        const pendingId = pending.sessionId || pending.id;
        api.get(`/api/sessions/${pendingId}/qr`).then((qrRes) => {
          if (qrRes?.data?.qr) displayQr(qrRes.data.qr, pendingId);
        }).catch(() => {});
      }
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
    } finally {
      setLoadingSessions(false);
    }
  };

  const fetchManagedProxy = async () => {
    try {
      const res = await api.get("/api/managed-proxy-addon");
      setManagedProxy(res?.data?.data || null);
    } catch (err) {
      console.error("Failed to fetch managed proxy add-on:", err);
    }
  };

  useEffect(() => {
    fetchSessions();
    fetchManagedProxy();
    socket.on("session_update", fetchSessions);
    return () => {
      socket.off("session_update", fetchSessions);
      // Never delete a pending WhatsApp session just because this component
      // unmounted. Mobile browsers can remount pages while the QR is visible,
      // and React development checks also intentionally remount effects. The
      // explicit Cancel action below owns destructive pending-session cleanup.
    };
  }, []);

  useEffect(() => {
    const onQr = ({ qr, sessionId, shortId }) => {
      // The backend's redisEventRelay already routes events to this user's
      // socket room — any qr we receive belongs to a session this user owns.
      // The previous `currentSessionRef.current` gate silently dropped QRs when
      // the user reconnected from the existing-session row (which doesn't set
      // currentSessionRef) or after a page refresh mid-flow.
      displayQr(qr, sessionId);
    };

    const onConnected = ({ sessionId }) => {
      if (sessionId === qrSessionId || sessionId === currentSessionRef.current) {
        setQrCode(null);
        setQrSessionId(null);
        setQrLoading(false);
        setLoginSuccess(true);
        // Successfully connected — no longer pending
        pendingSessionRef.current = null;
      }
    };

    const onDisconnected = ({ sessionId }) => {
      // An older worker socket can finish closing after a replacement socket
      // has already produced a fresh QR for the same session. Keep the active
      // QR visible; qr_expired is the authoritative end of a pairing attempt.
      if (sessionId === pendingSessionRef.current) return;
      if (sessionId === currentSessionRef.current) {
        setLoginSuccess(false);
        setQrCode(null);
        setQrSessionId(null);
        setQrLoading(false);
        pendingSessionRef.current = null;
      }
    };

    const onQrExpired = ({ sessionId }) => {
      if (sessionId === currentSessionRef.current || sessionId === pendingSessionRef.current) {
        setQrCode(null);
        setQrSessionId(null);
        setQrLoading(false);
        pendingSessionRef.current = null;
        currentSessionRef.current = null;
        modal.alert("QR code expired. Please try connecting again.", "warning");
        fetchSessions();
      }
    };

    socket.on("qr", onQr);
    socket.on("connected", onConnected);
    socket.on("disconnected", onDisconnected);
    socket.on("qr_expired", onQrExpired);

    return () => {
      socket.off("qr", onQr);
      socket.off("connected", onConnected);
      socket.off("disconnected", onDisconnected);
      socket.off("qr_expired", onQrExpired);
    };
  }, []);

  const handleConnect = async (targetPhone = null, targetProxyMode = null) => {
    if (connectInFlightRef.current) return;

    const number = normalizePhone(targetPhone || phone);
    if (!number) return modal.alert("Enter a valid phone number", "warning");
    const selectedProxyMode = targetProxyMode || proxyMode;

    if (!targetPhone && selectedProxyMode === "direct") {
      const confirmed = await modal.confirm(
        "Connect without a proxy? WhatsApp will see Walytic's server IP, which can increase the chance of a review or restriction. You can add a proxy later.",
        { confirmText: "Connect directly" }
      );
      if (!confirmed) return;
    }

    connectInFlightRef.current = true;
    setConnectMethod("qr");
    currentSessionRef.current = number;
    setSessionId(number);

    setLoginSuccess(false);
    setQrCode(null);
    setQrSessionId(number);
    // Surface a loading state immediately. Baileys can take 3–8s to boot
    // and emit the first QR — without this the user stares at an empty
    // panel and reports "QR is not visible".
    setQrLoading(true);

    try {
      const body = {};
      const trimmedProxyUrl = proxyUrl.trim();
      body.proxyMode = selectedProxyMode;
      if (!targetPhone && selectedProxyMode === "own") {
        if (!trimmedProxyUrl) throw new Error("Enter and test your proxy URL, or choose Walytic Managed Proxy.");
        body.proxyUrl = trimmedProxyUrl;
      }
      if (selectedProxyMode === "direct") body.proxyUrl = "";
      await api.post(`/api/sessions/${number}`, body);
      if (!targetPhone) setProxyUrl("");
      await fetchSessions();
      // Belt-and-braces poll of the cached-QR endpoint. The socket `qr`
      // event is the primary delivery path; this catches the race where
      // Baileys emits before the listener mounts or while the socket
      // is mid-reconnect. Stops as soon as setQrCode fires from the
      // socket handler (cleared by the polling closure below).
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        if (currentSessionRef.current !== number) return; // user navigated away
        try {
          const res = await api.get(`/api/sessions/${number}/qr`);
          if (res?.data?.qr) {
            // Only set if a live socket emit hasn't already populated it.
            setQrCode((prev) => prev || res.data.qr);
            setQrLoading(false);
            return;
          }
        } catch {}
        if (attempts < 12) setTimeout(poll, 1500); // ~18s window
        else setQrLoading(false);
      };
      setTimeout(poll, 1500);
    } catch (err) {
      console.error("Failed to start session:", err);
      modal.alert(err?.response?.data?.message || err?.message || "Failed to start session", "error");
      currentSessionRef.current = null;
      setSessionId(null);
      setQrSessionId(null);
      setQrLoading(false);
    } finally {
      connectInFlightRef.current = false;
    }
  };

  const handleLogout = async (id) => {
    try {
      await api.delete(`/api/sessions/${id}?force=true`);
      modal.alert("Session removed", "success");
      if (currentSessionRef.current === id) {
        currentSessionRef.current = null;
        setQrCode(null);
        setQrSessionId(null);
        setLoginSuccess(false);
      }
      fetchSessions();
    } catch (err) {
      console.error("Logout failed", err);
      modal.alert("Logout failed", "error");
    }
  };

  const handleCancelQr = async () => {
    const sessionToCancel = pendingSessionRef.current || currentSessionRef.current;
    if (!sessionToCancel) return;

    try {
      await api.delete(`/api/sessions/${sessionToCancel}?force=true`);
    } catch (err) {
      console.error("Cancel session failed", err);
    }
    pendingSessionRef.current = null;
    currentSessionRef.current = null;
    setQrCode(null);
    setQrSessionId(null);
    setPhone("");
    fetchSessions();
  };

  const handleRepair = async (id, number) => {
    const ok = await modal.confirm(
      `Force re-pair ${number}? This logs out the current WhatsApp link and shows a new QR code. Use this when messages are sending successfully but recipients aren't receiving them.`,
      { danger: false, confirmText: "Re-pair" }
    );
    if (!ok) return;
    try {
      await api.post(`/api/sessions/${id}/repair`);
      modal.alert("Session reset — scan the new QR with your phone", "success");
      currentSessionRef.current = id;
      pendingSessionRef.current = id;
      setQrSessionId(id);
      fetchSessions();
    } catch (err) {
      console.error("Repair failed", err);
      modal.alert("Re-pair failed — try Disconnect and Connect manually", "error");
    }
  };

  const handleToggleRejectCalls = async (session) => {
    const id = session.sessionId || session.id;
    const next = !session.rejectCalls;
    // Optimistic update so the toggle feels instant.
    setSessions((prev) =>
      prev.map((s) =>
        (s.sessionId || s.id) === id ? { ...s, rejectCalls: next } : s
      )
    );
    try {
      await api.patch(`/api/sessions/${id}/reject-calls`, { enabled: next });
    } catch (err) {
      console.error("toggle reject-calls failed", err);
      modal.alert("Failed to update call-reject setting", "error");
      // Roll back on failure.
      setSessions((prev) =>
        prev.map((s) =>
          (s.sessionId || s.id) === id ? { ...s, rejectCalls: !next } : s
        )
      );
    }
  };

  const handleEditRejectMessage = async (session) => {
    const id = session.sessionId || session.id;
    const current = session.rejectCallsMessage || "Sorry, I can't take calls right now. Please send a message instead.";
    const next = await modal.prompt(
      "Custom call-decline message",
      {
        defaultValue: current,
        confirmText: "Save",
        description: "Sent automatically when an incoming call is rejected. Keep it short.",
      }
    );
    if (next === null || next === undefined) return;
    try {
      await api.patch(`/api/sessions/${id}/reject-calls`, { message: next });
      modal.alert("Saved", "success");
      fetchSessions();
    } catch (err) {
      console.error("save reject-calls message failed", err);
      modal.alert("Failed to save", "error");
    }
  };

  const handleTestProxyUrl = async () => {
    const trimmedProxyUrl = proxyUrl.trim();
    if (!trimmedProxyUrl) return modal.alert("Enter a proxy URL to test", "warning");
    setProxyTesting(true);
    try {
      const res = await api.post("/api/sessions/proxy/test", { proxyUrl: trimmedProxyUrl });
      modal.alert(res?.data?.message || "Proxy test succeeded", "success");
    } catch (err) {
      console.error("proxy test failed", err);
      modal.alert(err?.response?.data?.message || "Proxy test failed", "error");
    } finally {
      setProxyTesting(false);
    }
  };

  const handleTestSessionProxy = async (session) => {
    const id = session.sessionId || session.id;
    setTestingProxySession(id);
    try {
      const res = await api.post(`/api/sessions/${id}/proxy/test`);
      modal.alert(res?.data?.message || "Proxy test succeeded", "success");
    } catch (err) {
      console.error("stored proxy test failed", err);
      modal.alert(err?.response?.data?.message || "Proxy test failed", "error");
    } finally {
      setTestingProxySession(null);
    }
  };

  const handleEditProxy = async (session) => {
    const id = session.sessionId || session.id;
    const isManaged = session.managedProxy === true;
    const current = isManaged
      ? "Walytic Managed Proxy — host and credentials hidden"
      : session.proxyUrlMasked || "Direct connection";
    const next = await modal.prompt(
      isManaged ? "Replace with your own proxy" : "Web connection proxy",
      {
        defaultValue: "",
        inputType: "password",
        autoComplete: "new-password",
        confirmText: "Save",
        description: isManaged
          ? `Current: ${current}. Enter an http://, https://, socks4://, or socks5:// URL to replace it, or leave blank to connect directly. Walytic never sends managed proxy details to your browser.`
          : `Current: ${current}. Enter http://, https://, socks4://, or socks5:// proxy URL. Leave blank to clear.`,
      }
    );
    if (next === null || next === undefined) return;
    try {
      const res = await api.patch(`/api/sessions/${id}/proxy`, { proxyUrl: next.trim() });
      modal.alert(res?.data?.message || "Proxy saved", "success");
      fetchSessions();
    } catch (err) {
      console.error("save proxy failed", err);
      modal.alert(err?.response?.data?.message || "Failed to save proxy", "error");
    }
  };

  const handleAssignManagedProxy = async (session) => {
    const id = session.sessionId || session.id;
    try {
      const res = await api.post(`/api/managed-proxy-addon/sessions/${id}/assign`);
      modal.alert(res?.data?.message || "Managed proxy assigned", "success");
      await Promise.all([fetchSessions(), fetchManagedProxy()]);
    } catch (err) {
      modal.alert(err?.response?.data?.message || "Could not assign a managed proxy seat", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!(await modal.confirm("Are you sure you want to permanently delete this session?", { danger: true, confirmText: "Delete" }))) return;

    try {
      await api.delete(`/api/sessions/${id}?force=true`);
      modal.alert("Session deleted successfully", "success");
      if (currentSessionRef.current === id) {
        currentSessionRef.current = null;
        setQrCode(null);
        setQrSessionId(null);
        setLoginSuccess(false);
      }
      fetchSessions();
    } catch (err) {
      console.error("Delete failed", err);
      modal.alert("Delete failed", "error");
    }
  };

  const statusBadge = (session) => {
    const status = session.status;
    if (status === "connected") return <span className="badge-success">Connected</span>;
    if (status === "pending") return <span className="badge-warning">Pending</span>;
    if (status === "degraded") {
      const d = session.delivery;
      const tip = d
        ? `${d.acked} of last ${d.sent} messages reached recipients (${Math.round((d.rate || 0) * 100)}%). ${d.stuckSent} stuck > 60s without delivery. Likely cause: WhatsApp unlinked Walytic from your phone. Re-pair to fix.`
        : "Recent messages aren't reaching recipients. Re-pair recommended.";
      return (
        <span
          className="badge-warning"
          title={tip}
          style={{ cursor: "help" }}
        >
          ⚠ Delivery issues
        </span>
      );
    }
    return <span className="badge-danger">{status}</span>;
  };

  const activeManagedProxy = managedProxy?.subscription?.status === "active";
  const managedSeatAvailable = activeManagedProxy && (managedProxy?.subscription?.availableSeats || 0) > 0;

  return (
    <div className="page-content">
      <PageHeader
        title="WhatsApp Connections"
        subtitle="Manage your WhatsApp client sessions"
      />

      {loginSuccess && <Alert variant="success" className="mb-4">Login successful!</Alert>}

      {sessions.some((s) => s.status === "degraded") && (
        <Alert variant="warning" className="mb-4">
          <strong>Delivery issues detected.</strong>{" "}
          {sessions.filter((s) => s.status === "degraded").map((s) => s.number).join(", ")}{" "}
          {sessions.filter((s) => s.status === "degraded").length === 1 ? "is" : "are"}{" "}
          accepting messages but recipients aren't receiving them. WhatsApp may have
          unlinked Walytic from your phone. Disconnect and re-pair to fix.
        </Alert>
      )}

      <Card className="mb-6">
        <CardHeader className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaWhatsapp className="text-brand" />
            <span>Connected WhatsApp</span>
          </div>
          <span className="text-sm text-ink-muted">
            Quota: {usedConnections} / {maxConnections === -1 ? "∞" : maxConnections}
          </span>
        </CardHeader>
        <CardBody className="p-0">
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Client ID</Th>
                  <Th>Number</Th>
                  <Th>Status</Th>
                  <Th>Proxy</Th>
                  <Th>Pending</Th>
                  <Th>Last Message</Th>
                  <Th title="Antiban: auto-reject incoming calls and reply with a polite text. Recommended ON to avoid suspicious unanswered-call patterns.">Reject Calls</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr key={session.sessionId || session.id}>
                    <Td className="font-mono text-xs">{session.sessionId || session.id}</Td>
                    <Td>{session.number}</Td>
                    <Td>{statusBadge(session)}</Td>
                    <Td>
                      {session.proxyConfigured ? (
                        <div className="flex items-center gap-2">
                          <span className={session.managedProxy ? "badge-success" : "badge-info"} title={session.proxyUrlMasked || (session.managedProxy ? "Credentials managed securely by Walytic" : "Proxy configured")}>
                            {session.managedProxy ? "Managed" : "Own proxy"}
                          </span>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            loading={testingProxySession === (session.sessionId || session.id)}
                            onClick={() => handleTestSessionProxy(session)}
                          >
                            Test
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-ink-muted">Direct</span>
                      )}
                    </Td>
                    <Td>{session.pendingMessages}</Td>
                    <Td>{session.lastMessageTime}</Td>
                    <Td>
                      <label className="inline-flex items-center gap-2 cursor-pointer" title="Auto-reject incoming calls and reply with a polite text">
                        <input
                          type="checkbox"
                          checked={session.rejectCalls !== false}
                          onChange={() => handleToggleRejectCalls(session)}
                          className="form-checkbox h-4 w-4"
                        />
                        <span className="text-xs text-ink-secondary">
                          {session.rejectCalls !== false ? "On" : "Off"}
                        </span>
                      </label>
                    </Td>
                    <Td>
                      <select
                        className="input text-sm py-1 px-2"
                        onChange={(e) => {
                          // Always reconnect by stable sessionId (Client ID), not
                          // display number. Number can differ from sessionId when
                          // the user originally typed a local format; using number
                          // here used to mint a brand-new sessionId every reconnect.
                          const sid = session.sessionId || session.id;
                          const reconnectMode = session.proxySource === "direct" ? "direct" : "managed";
                          if (e.target.value === "connect") handleConnect(sid, reconnectMode);
                          if (e.target.value === "showqr") handleConnect(sid, reconnectMode);
                          if (e.target.value === "disconnect") handleLogout(sid);
                          if (e.target.value === "repair") handleRepair(sid, session.number || sid);
                          if (e.target.value === "delete") handleDelete(sid);
                          if (e.target.value === "rejectmsg") handleEditRejectMessage(session);
                          if (e.target.value === "proxy") handleEditProxy(session);
                          if (e.target.value === "managed-proxy") handleAssignManagedProxy(session);
                          e.target.value = "actions";
                        }}
                      >
                        <option value="actions">Actions</option>
                        {session.status !== "connected" && session.status !== "degraded" && (
                          <>
                            <option value="connect">Connect</option>
                            <option value="showqr">Show QR</option>
                          </>
                        )}
                        {(session.status === "connected" || session.status === "degraded") && (
                          <option value="disconnect">
                            {session.status === "degraded" ? "Disconnect (then re-pair)" : "Disconnect"}
                          </option>
                        )}
                        {session.status === "degraded" && (
                          <option value="repair">Force re-pair (one-click fix)</option>
                        )}
                        <option value="rejectmsg">Edit call-decline message…</option>
                        {!session.proxyConfigured && activeManagedProxy && <option value="managed-proxy">Protect with Managed Proxy</option>}
                        <option value="proxy">{session.managedProxy ? "Change proxy or connect directly..." : "Edit proxy..."}</option>
                        {!isTeamMember && <option value="delete">Delete</option>}
                      </select>
                    </Td>
                  </tr>
                ))}
                {sessions.length === 0 && (
                  <tr>
                    <Td colSpan="8" className="text-center text-ink-muted py-8">
                      {loadingSessions ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="inline-block w-3 h-3 rounded-full border-2 border-brand border-t-transparent animate-spin" />
                          Loading your WhatsApp sessions…
                        </span>
                      ) : (
                        "No sessions yet"
                      )}
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableWrapper>
        </CardBody>
      </Card>

      <OnboardingTip id="whatsapp-connect-guide" title="Getting Started" step="Step 1">
        Enter your WhatsApp phone number below and click Connect. A QR code will appear. Open WhatsApp on your phone, go to <strong>Settings &gt; Linked Devices &gt; Link a Device</strong>, and scan the QR code. Your WhatsApp will be connected within seconds.
      </OnboardingTip>

      <Card className="mt-3">
        <CardHeader className="flex items-center gap-2">
          <FaPlug className="text-brand" />
          <span>Start New WhatsApp Client</span>
        </CardHeader>
        <CardBody>
          {/* Step 0: choose how to connect */}
          {connectMethod === null && (
            <div>
              <p className="text-sm text-ink-muted mb-4">How do you want to connect?</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={() => setConnectMethod("qr")}
                  className="flex flex-col items-start gap-2 p-5 rounded-xl border border-surface-border bg-surface-card hover:border-brand hover:bg-brand/5 transition-colors text-left"
                >
                  <FaQrcode size={22} className="text-brand" />
                  <span className="font-semibold text-ink-primary">Via QR Code</span>
                  <span className="text-xs text-ink-muted">
                    Link a device with your phone: WhatsApp → Settings → Linked Devices → Link a Device.
                  </span>
                </button>
                <button
                  onClick={() => setConnectMethod("extension")}
                  className="flex flex-col items-start gap-2 p-5 rounded-xl border border-surface-border bg-surface-card hover:border-brand hover:bg-brand/5 transition-colors text-left"
                >
                  <FaChrome size={22} className="text-brand" />
                  <span className="font-semibold text-ink-primary">Via Extension</span>
                  <span className="text-xs text-ink-muted">
                    Use your already-logged-in WhatsApp Web session — no QR code, no datacenter IP.
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* QR flow (existing) */}
          {connectMethod === "qr" && (
            <>
              <button
                onClick={() => setConnectMethod(null)}
                className="flex items-center gap-1 text-xs text-ink-muted hover:text-brand transition-colors mb-4"
              >
                <FaArrowLeft size={10} /> Choose another method
              </button>
              <div className="input-group mb-2">
                <label className="input-label">
                  WhatsApp Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g. 918367777110"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
                />
                <p className="text-xs text-ink-muted mt-1.5">
                  Enter digits only with country code. No +, spaces, or dashes. Examples: <span className="font-mono text-ink-secondary">918367777110</span> (India), <span className="font-mono text-ink-secondary">12025551234</span> (US), <span className="font-mono text-ink-secondary">971581209232</span> (UAE)
                </p>
              </div>
              <div className="input-group mb-4">
                <label className="input-label">Choose how this connection reaches WhatsApp <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setProxyMode("managed")}
                    className={`rounded-xl border p-4 text-left transition-colors ${proxyMode === "managed" ? "border-brand bg-brand/5" : "border-surface-border bg-surface-card"}`}
                  >
                    <div className="flex items-center gap-2 font-semibold text-ink-primary"><FaShieldAlt className="text-brand" /> Walytic Proxy</div>
                    <p className="mt-1 text-xs text-ink-muted">Included free for QR connections. Credentials stay private on Walytic's servers.</p>
                    <p className={`mt-2 text-xs font-medium ${managedSeatAvailable ? "text-violet-700" : "text-green-700"}`}>
                      {managedSeatAvailable
                        ? `Premium queue · ${managedProxy.subscription.availableSeats} seat${managedProxy.subscription.availableSeats === 1 ? "" : "s"} available`
                        : activeManagedProxy ? "Free queue · premium seats already assigned" : "Free queue included"}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setProxyMode("own")}
                    className={`rounded-xl border p-4 text-left transition-colors ${proxyMode === "own" ? "border-brand bg-brand/5" : "border-surface-border bg-surface-card"}`}
                  >
                    <div className="flex items-center gap-2 font-semibold text-ink-primary"><FaKey className="text-brand" /> Bring your own proxy</div>
                    <p className="mt-1 text-xs text-ink-muted">Free. Supply an HTTP, HTTPS, SOCKS4, or SOCKS5 URL and test it before connecting.</p>
                  </button>
                </div>
              </div>

              {proxyMode === "managed" && (
                <div className="mb-4">
                  <Alert variant="info" className="mb-3">
                    Walytic automatically uses the premium queue for active $5 seats and the free queue for everyone else.
                  </Alert>
                  <ManagedProxyAddonCard compact onChanged={fetchManagedProxy} />
                </div>
              )}

              {proxyMode === "own" && (
                <div className="input-group mb-4">
                  <label className="input-label">Your proxy URL</label>
                  <div className="flex gap-2">
                    <input
                      type="password"
                      autoComplete="off"
                      className="input flex-1 min-w-0"
                      placeholder="http://user:pass@host:8080 or socks5://host:1080"
                      value={proxyUrl}
                      onChange={(e) => setProxyUrl(e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      loading={proxyTesting}
                      disabled={!proxyUrl.trim() || proxyTesting}
                      onClick={handleTestProxyUrl}
                    >
                      Test
                    </Button>
                  </div>
                  <p className="text-xs text-ink-muted mt-1.5">Encrypted at rest. Walytic never returns the password after saving.</p>
                </div>
              )}
              <Button
                variant="primary"
                onClick={() => handleConnect()}
                loading={qrLoading}
                disabled={qrLoading || !!qrCode || isExpired || connectionQuotaReached || (proxyMode === "own" ? !proxyUrl.trim() : false)}
              >
                <FaPlug size={12} className="mr-2" />
                Connect
              </Button>

              {qrLoading && !qrCode && (
                <div className="mt-6 p-4 bg-surface-bg rounded-lg text-center">
                  <div className="flex items-center gap-2 justify-center mb-3">
                    <FaQrcode className="text-brand animate-pulse" />
                    <h3 className="font-semibold text-ink-primary">Generating QR code…</h3>
                  </div>
                  <p className="text-sm text-ink-muted">
                    This usually takes 3–8 seconds. Keep this tab open.
                  </p>
                </div>
              )}

              {qrCode && (
                <div className="mt-6 p-4 bg-surface-bg rounded-lg text-center">
                  <div className="flex items-center gap-2 justify-center mb-3">
                    <FaQrcode className="text-brand" />
                    <h3 className="font-semibold text-ink-primary">Scan QR Code</h3>
                  </div>
                  <p className="text-sm text-ink-muted mb-3">Session: {qrSessionId}</p>
                  <QRCodeCanvas
                    value={qrCode}
                    size={250}
                    level="M"
                    marginSize={2}
                    title="Scan this QR code with WhatsApp"
                    className="mx-auto rounded-lg"
                  />
                  <Button
                    variant="danger"
                    className="mt-4"
                    onClick={handleCancelQr}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Extension flow */}
          {connectMethod === "extension" && (
            <div>
              <button
                onClick={() => setConnectMethod(null)}
                className="flex items-center gap-1 text-xs text-ink-muted hover:text-brand transition-colors mb-4"
              >
                <FaArrowLeft size={10} /> Choose another method
              </button>

              <div className="flex flex-col sm:flex-row items-start gap-4 p-4 rounded-xl border border-surface-border bg-surface-bg">
                <FaChrome size={32} className="text-brand flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-ink-primary">Walytic Browser Relay</h3>
                  <p className="text-sm text-ink-muted mt-1">
                    Relays your logged-in WhatsApp Web session to Walytic — lower ban risk, your own IP, no QR code.
                  </p>

                  <Button
                    variant="primary"
                    className="mt-3"
                    onClick={() => window.open(EXTENSION_DOWNLOAD_URL, "_blank", "noopener,noreferrer")}
                  >
                    <FaDownload size={12} className="mr-2" />
                    Download extension
                  </Button>

                  <ol className="mt-4 space-y-2 text-sm text-ink-secondary">
                    <li><span className="font-semibold">1.</span> Install the extension from the Chrome Web Store.</li>
                    <li><span className="font-semibold">2.</span> Open <span className="font-mono">web.whatsapp.com</span> and log in (keep the tab open).</li>
                    <li><span className="font-semibold">3.</span> Click the extension icon and sign in with your Walytic email &amp; password.</li>
                  </ol>

                  <Link
                    to="/browser/sessions"
                    className="inline-block mt-4 text-sm text-brand hover:underline"
                  >
                    View connected browsers →
                  </Link>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default WhatsappPhones;
